import { Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { apiGet, apiPost } from "@/lib/client/api";
import type { ModuleSeed } from "@/lib/db/seed/modules";
import type { TemplateSeed } from "@/lib/db/seed/templates";
import { buildCreateQuotationPayload, getDefaultSelectedModuleCodes, toggleModuleCode } from "@/lib/quotations/create-form";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";
import { cn } from "@/lib/utils/cn";

type TemplatesResponse = {
  templates: TemplateSeed[];
};

type ModulesResponse = {
  modules: ModuleSeed[];
};

type CreateQuotationResponse = {
  id: string;
  quotationNo: string;
};

export function QuoteNewPage() {
  const { language, t } = useAppSettings();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<TemplateSeed[]>([]);
  const [modules, setModules] = useState<ModuleSeed[]>([]);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState("");
  const [selectedModuleCodes, setSelectedModuleCodes] = useState<string[]>([]);
  const [projectTitle, setProjectTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedTemplate = templates.find((template) => template.code === selectedTemplateCode);
  const sortedModules = useMemo(() => [...modules].sort((left, right) => left.sortOrder - right.sortOrder), [modules]);

  useEffect(() => {
    let active = true;

    async function loadCatalog() {
      setLoading(true);
      setError(null);

      try {
        const [templatesResponse, modulesResponse] = await Promise.all([
          apiGet<TemplatesResponse>("/api/templates"),
          apiGet<ModulesResponse>("/api/modules"),
        ]);

        if (!active) {
          return;
        }

        const firstTemplate = templatesResponse.templates[0];
        setTemplates(templatesResponse.templates);
        setModules(modulesResponse.modules);
        setSelectedTemplateCode(firstTemplate?.code ?? "");
        setSelectedModuleCodes(firstTemplate ? getDefaultSelectedModuleCodes(firstTemplate) : []);
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : t("quoteNew.loadError"));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadCatalog();

    return () => {
      active = false;
    };
  }, [t]);

  function handleSelectTemplate(template: TemplateSeed) {
    setSelectedTemplateCode(template.code);
    setSelectedModuleCodes(getDefaultSelectedModuleCodes(template));
    setError(null);
  }

  function handleToggleModule(moduleCode: string) {
    setSelectedModuleCodes((current) => toggleModuleCode(current, moduleCode));
    setError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const payload = buildCreateQuotationPayload({
      templateCode: selectedTemplateCode,
      projectTitle,
      selectedModuleCodes,
    });

    if (!payload.ok) {
      setError(payload.error);
      return;
    }

    setSubmitting(true);

    try {
      const response = await apiPost<typeof payload.data, CreateQuotationResponse>("/api/quotations", payload.data);
      navigate(workbenchRoutes.quotationEdit(response.id));
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : t("quoteNew.submitError"));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedModuleCount = selectedModuleCodes.length;

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <div>
        <h1 className="text-2xl font-semibold">{t("quoteNew.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("quoteNew.description")}</p>
      </div>

      {error ? <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div> : null}

      <Card className="p-4">
        <label className="mb-2 block text-sm font-medium" htmlFor="project-title">
          {t("quoteNew.projectTitle")}
        </label>
        <Input
          id="project-title"
          value={projectTitle}
          onChange={(event) => setProjectTitle(event.target.value)}
          placeholder={t("quoteNew.projectTitlePlaceholder")}
        />
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {templates.map((template) => (
          <Card key={template.code} className={cn("p-4 transition", selectedTemplateCode === template.code && "border-primary bg-primary/5")}>
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="font-medium">{language === "zh" ? template.nameZh : template.nameEn}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{template.projectType}</p>
              </div>
              {selectedTemplateCode === template.code ? <Check className="h-4 w-4 text-primary" aria-hidden="true" /> : null}
            </div>
            <Button type="button" variant={selectedTemplateCode === template.code ? "primary" : "secondary"} className="w-full" onClick={() => handleSelectTemplate(template)}>
              {selectedTemplateCode === template.code ? t("quoteNew.selected") : t("quoteNew.select")}
            </Button>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-medium">{t("quoteNew.modules")}</h2>
            <p className="text-sm text-muted-foreground">
              {selectedTemplate ? `${language === "zh" ? selectedTemplate.nameZh : selectedTemplate.nameEn} · ${selectedModuleCount}` : t("quoteNew.noTemplate")}
            </p>
          </div>
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" /> : null}
        </div>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {sortedModules.map((module) => {
            const checked = selectedModuleCodes.includes(module.code);

            return (
              <label
                key={module.code}
                className={cn(
                  "flex min-h-11 cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 text-sm transition hover:bg-muted",
                  checked && "border-primary bg-primary/5 text-foreground",
                )}
              >
                <input type="checkbox" checked={checked} onChange={() => handleToggleModule(module.code)} className="h-4 w-4 accent-primary" />
                <span>{language === "zh" ? module.titleZh : module.titleEn}</span>
              </label>
            );
          })}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={loading || submitting}>
          {submitting ? t("quoteNew.creating") : t("quoteNew.create")}
        </Button>
      </div>
    </form>
  );
}
